import { useCallback, useEffect, useState } from "react";
import {
    fetchMe,
    login as apiLogin,
    logout as apiLogout,
    register as apiRegister,
    type AuthUser,
} from "../api/auth";
import { extractApiErrorMessage } from "../api/client";

interface UseAuthResult {
    user: AuthUser | null;
    ready: boolean;
    error: string | null;
    login: (username: string, password: string, rememberMe: boolean) => Promise<void>;
    register: (username: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
}

/**
 * Tracks the optional logged-in-user identity, entirely separate from (and
 * coexisting with) the anonymous guest identity `useSession` tracks - the
 * guest cookie keeps being issued regardless of login state. Bootstraps on
 * mount via GET /auth/me, which always resolves (200) whether authenticated
 * or not, so `ready` is tracked independently rather than derived from
 * `user !== null`.
 */
export function useAuth(): UseAuthResult {
    const [user, setUser] = useState<AuthUser | null>(null);
    const [ready, setReady] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        fetchMe()
            .then((info) => {
                if (!cancelled) {
                    setUser(info.authenticated ? info.user : null);
                    setReady(true);
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    setError(extractApiErrorMessage(err, "Impossibile verificare l'accesso."));
                    setReady(true);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const login = useCallback(async (username: string, password: string, rememberMe: boolean) => {
        const session = await apiLogin(username, password, rememberMe);
        setUser(session.user);
    }, []);

    const register = useCallback(async (username: string, password: string) => {
        const session = await apiRegister(username, password);
        setUser(session.user);
    }, []);

    const logout = useCallback(async () => {
        await apiLogout();
        setUser(null);
    }, []);

    return { user, ready, error, login, register, logout };
}
